/**
 * Reusable Tooltip Web Component
 * Provides hover-based tooltip functionality using the native popover API
 * Dynamically adapts interaction patterns based on device capabilities
 */
class TooltipComponent extends HTMLElement {
  #resizeHandler = null;
  #backdropClickHandler = null;
  #mediaQueryHandler = null;
  #hoverMediaQuery = null;
  #isMobile = false;
  #isTooltipOpen = false;

  constructor() {
    super();
    this.tooltip = null;
    this.trigger = null;
  }

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#removeEventListeners();
    this.#removeMediaQueryListener();
  }

  #init() {
    this.#createElements();
    this.parentNode.classList.remove('hidden');
    this.#setupMediaQueryDetection();
    this.#addEventListeners();
  }

  #setupMediaQueryDetection() {
    // Create media query to detect hover capability
    this.#hoverMediaQuery = window.matchMedia('(hover: hover)');
    this.#isMobile = !this.#hoverMediaQuery.matches;
    
    // Create handler for media query changes
    this.#mediaQueryHandler = (event) => {
      const wasMobile = this.#isMobile;
      this.#isMobile = !event.matches;
      
      // Only rebind events if the interaction mode actually changed
      if (wasMobile !== this.#isMobile) {
        console.log(`Interaction mode changed: ${this.#isMobile ? 'mobile' : 'desktop'}`);
        this.#removeInteractionEventListeners();
        this.#addInteractionEventListeners();
      }
    };
    
    // Listen for changes in hover capability (device rotation, window resize, etc.)
    this.#hoverMediaQuery.addEventListener('change', this.#mediaQueryHandler);
  }

  #removeMediaQueryListener() {
    if (this.#hoverMediaQuery && this.#mediaQueryHandler) {
      this.#hoverMediaQuery.removeEventListener('change', this.#mediaQueryHandler);
    }
  }

  #createElements() {
    // Get content from slots or attributes
    const triggerText = this.getAttribute('trigger-text') || this.querySelector('[slot="trigger"]')?.textContent || 'Info';
    const title = this.getAttribute('title') || this.querySelector('[slot="title"]')?.textContent || '';
    const content = this.getAttribute('content') || this.querySelector('[slot="content"]')?.innerHTML || '';

    // Generate unique IDs
    const triggerId = `tooltip-trigger-${this.#generateId()}`;
    const popoverId = `tooltip-popover-${this.#generateId()}`;

    // Create trigger element
    this.trigger = document.createElement('div');
    this.trigger.id = triggerId;
    this.trigger.setAttribute('popovertarget', popoverId);
    this.trigger.className = 'tooltip__trigger';
    this.trigger.textContent = triggerText;

    // Create popover element
    this.tooltip = document.createElement('div');
    this.tooltip.id = popoverId;
    this.tooltip.setAttribute('popover', '');
    this.tooltip.className = 'tooltip__popover';

    // Create popover content
    let popoverContent = '';
    if (title) {
      popoverContent += `<h5 class="tooltip__title">${title}</h5>`;
    }
    popoverContent += `<div class="tooltip__content">${content}</div>`;
    
    this.tooltip.innerHTML = popoverContent;

    // Clear existing content and append new elements
    this.innerHTML = '';
    const fragment = document.createDocumentFragment();
    fragment.appendChild(this.trigger);
    fragment.appendChild(this.tooltip);
    this.appendChild(fragment);
    
    // Just set popover attribute - no custom backdrop needed!
    this.tooltip.setAttribute('popover', '');
  }

  #addEventListeners() {
    if (!this.trigger || !this.tooltip) return;

    // Add interaction-specific event listeners
    this.#addInteractionEventListeners();
    
    // Ensure popover uses auto mode for proper backdrop behavior
    this.tooltip.setAttribute('popover', 'auto');
    
    // Listen for popover toggle events
    this.tooltip.addEventListener('toggle', (event) => {
      this.#isTooltipOpen = event.newState === 'open';
      
      if (event.newState === 'open') {
        // Add backdrop click handler after a brief delay to avoid immediate closure
        setTimeout(() => {
          this.#addBackdropClickHandler();
        }, 10);
      } else {
        this.#removeBackdropClickHandler();
      }
    });
    
    // Reposition tooltip on window resize
    this.#resizeHandler = this.#debounce(() => {
      if (this.tooltip?.matches(':popover-open')) {
        this.#positionTooltip();
      }
    }, 16);
    window.addEventListener('resize', this.#resizeHandler);
  }

  #addInteractionEventListeners() {
    if (!this.trigger) return;

    if (this.#isMobile) {
      // Mobile: Use click to toggle tooltip
      this.trigger.addEventListener('click', this.#toggleTooltip.bind(this));
      
      // Prevent the click from immediately closing via backdrop
      this.trigger.addEventListener('click', (event) => {
        event.stopPropagation();
      });
      
      // Add visual feedback for mobile taps
      this.trigger.classList.add('tooltip__trigger--mobile');
    } else {
      // Desktop: Use hover events
      this.trigger.addEventListener('mouseenter', this.#showTooltip.bind(this));
      this.trigger.addEventListener('mouseleave', this.#hideTooltip.bind(this));
      
      // Remove mobile-specific styling
      this.trigger.classList.remove('tooltip__trigger--mobile');
    }
  }

  #removeInteractionEventListeners() {
    if (!this.trigger) return;

    // Remove all possible event listeners
    this.trigger.removeEventListener('click', this.#toggleTooltip.bind(this));
    this.trigger.removeEventListener('mouseenter', this.#showTooltip.bind(this));
    this.trigger.removeEventListener('mouseleave', this.#hideTooltip.bind(this));
    
    // Remove click prevention handler
    const clickHandler = (event) => event.stopPropagation();
    this.trigger.removeEventListener('click', clickHandler);
  }

  #toggleTooltip(event) {
    if (!this.tooltip) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    if (this.#isTooltipOpen) {
      this.#hideTooltip();
    } else {
      this.#showTooltip();
    }
  }

  #addBackdropClickHandler() {
    // Handle clicks on the backdrop (outside the tooltip)
    this.#backdropClickHandler = (event) => {
      // Only close if click is outside the tooltip content and trigger
      if (!this.tooltip.contains(event.target) && !this.trigger.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        this.#hideTooltip();
      }
    };
    
    // Use capture phase to catch events before they bubble
    document.addEventListener('click', this.#backdropClickHandler, true);
  }

  #removeBackdropClickHandler() {
    if (this.#backdropClickHandler) {
      document.removeEventListener('click', this.#backdropClickHandler, true);
      this.#backdropClickHandler = null;
    }
  }

  #removeEventListeners() {
    this.#removeInteractionEventListeners();
    this.#removeBackdropClickHandler();
    
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
    }
  }

  #showTooltip() {
    if (!this.tooltip) return;
    
    this.tooltip.showPopover();
    // Use requestAnimationFrame to ensure popover is rendered before positioning
    requestAnimationFrame(() => {
      this.#positionTooltip();
    });
  }

  #hideTooltip() {
    if (!this.tooltip) return;
    this.tooltip.hidePopover();
  }

  #positionTooltip() {
    if (!this.tooltip || !this.trigger) return;

    const tooltipRect = this.tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 10;

    if (this.#isMobile) {
      // Mobile: Center the tooltip in the viewport
      const left = (viewportWidth - tooltipRect.width) / 2;
      const top = (viewportHeight - tooltipRect.height) / 2;

      // Ensure tooltip stays within viewport bounds
      const constrainedLeft = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));
      const constrainedTop = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

      this.tooltip.style.left = `${constrainedLeft}px`;
      this.tooltip.style.top = `${constrainedTop}px`;
      
      // Add mobile-specific class for styling
      this.tooltip.classList.add('tooltip__popover--mobile');
    } else {
      // Desktop: Position relative to trigger (existing logic)
      const triggerRect = this.trigger.getBoundingClientRect();
      const gap = 8;

      // Calculate position above the trigger, centered horizontally
      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      let top = triggerRect.top - tooltipRect.height - gap;

      // Adjust horizontal position if tooltip would go off-screen
      if (left < margin) {
        left = margin;
      } else if (left + tooltipRect.width > viewportWidth - margin) {
        left = viewportWidth - tooltipRect.width - margin;
      }

      // Adjust vertical position if tooltip would go off-screen above
      if (top < margin) {
        // Position below the trigger instead
        top = triggerRect.bottom + gap;
      }

      // Ensure tooltip doesn't go below viewport
      if (top + tooltipRect.height > viewportHeight - margin) {
        top = viewportHeight - tooltipRect.height - margin;
      }

      this.tooltip.style.left = `${left}px`;
      this.tooltip.style.top = `${top}px`;
      
      // Remove mobile-specific class
      this.tooltip.classList.remove('tooltip__popover--mobile');
    }
  }

  #generateId() {
    return Math.random().toString(36).substr(2, 9);
  }

  #debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Static method to register the component
  static register() {
    if (!customElements.get('tooltip-component')) {
      customElements.define('tooltip-component', TooltipComponent);
    }
  }
}

// Auto-register the component
TooltipComponent.register();
